import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from './data/config';
import { BootScene } from './scenes/BootScene';
import { PreloadScene } from './scenes/PreloadScene';
import { TitleScene } from './scenes/TitleScene';
import { CharacterSelectScene } from './scenes/CharacterSelectScene';
import { GameScene } from './scenes/GameScene';
import { GameOverScene } from './scenes/GameOverScene';
import { LevelClearedScene } from './scenes/LevelClearedScene';
import { HandoffScene } from './scenes/HandoffScene';
import { PlanetClearedScene } from './scenes/PlanetClearedScene';
import { LeaderboardScene } from './scenes/LeaderboardScene';

const config: Phaser.Types.Core.GameConfig = {
  type: Phaser.AUTO,
  parent: 'game',
  backgroundColor: '#0d0a1a',
  pixelArt: true,
  antialias: false,
  roundPixels: true,
  width: GAME_WIDTH,
  height: GAME_HEIGHT,
  scale: {
    mode: Phaser.Scale.FIT,
    autoCenter: Phaser.Scale.CENTER_BOTH,
  },
  physics: {
    default: 'arcade',
    arcade: {
      gravity: { x: 0, y: 1100 },
      debug: false,
    },
  },
  input: {
    activePointers: 4, // multitouch (joystick + buttons)
  },
  scene: [
    BootScene,
    PreloadScene,
    TitleScene,
    CharacterSelectScene,
    GameScene,
    GameOverScene,
    LevelClearedScene,
    HandoffScene,
    PlanetClearedScene,
    LeaderboardScene,
  ],
};

const game = new Phaser.Game(config);

// Expose on window in dev so we can poke at the running game from devtools.
if (import.meta.env.DEV) {
  (window as unknown as { __game: Phaser.Game }).__game = game;
}

// Bootstrap god mode. Default-on ONLY when running on localhost — production
// builds can't enable it via URL anymore (the `?god` URL flag was used during
// the Teams-webhook + leaderboard pipeline test phase, but now that the
// pipeline is verified we lock god mode and the F dev-shortcut out of prod
// entirely so random visitors can't farm invincibility + spam fake scores).
//
// To re-enable temporarily for staging tests, paste this in the devtools
// console: `window.__god = true` — `import.meta.env.DEV` is also a hard
// gate further down the call stack (the F shortcut in CharacterSelectScene
// still uses __god, but during a deploy you'd flip the gate manually).
if (typeof window !== 'undefined') {
  const host = window.location.hostname;
  const isLocal = host === 'localhost' || host === '127.0.0.1' || host === '';
  (window as unknown as { __god: boolean }).__god = isLocal;
}

// Hide the HTML splash once Phaser is up.
window.addEventListener('load', () => {
  const splash = document.getElementById('splash');
  if (splash) splash.style.display = 'none';
});

// Prevent context menu on long press for mobile.
window.addEventListener('contextmenu', (e) => e.preventDefault());

// On mobile Chrome the address bar slides in/out as the user scrolls — that
// changes the visible viewport without firing a normal window resize. We
// listen on visualViewport (which DOES fire) and tell Phaser to refresh
// the scale calc so the canvas re-centers in the new visible area.
// Without this the canvas can end up offset to one side after the address
// bar shows up post-load on real phones.
if (typeof window !== 'undefined' && 'visualViewport' in window && window.visualViewport) {
  const refresh = () => {
    game.scale.refresh();
  };
  window.visualViewport.addEventListener('resize', refresh);
  window.addEventListener('orientationchange', () => {
    // Orientation change needs a tick to settle before measuring.
    setTimeout(refresh, 100);
  });
}

// Suppress browser-default behavior for keys that aren't game inputs but
// are easy to fat-finger during gameplay. Examples: Tab opens browser focus
// hopping, F1 opens help, F3 opens find, '/' is Chrome's quick-find, etc.
// The whitelist below contains every key the game actually uses (movement,
// jump, fire, mute, pause, dev shortcuts). Anything else gets blocked
// UNLESS the focus is currently on an HTML input/textarea — that lets the
// score-entry form still accept normal typing.
//
// Note: this CANNOT block OS-level shortcuts (Cmd+T, Ctrl+W, Cmd+Q, etc.)
// — only Keyboard Lock API in fullscreen can. The fullscreen button on
// the planet-select screen handles that case.
const GAME_KEY_ALLOWLIST = new Set([
  'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
  // Game letters (movement, fire, jump, mute, pause, quit, dev shortcuts).
  'a', 'A', 'd', 'D', 'f', 'F', 'g', 'G', 'm', 'M',
  'p', 'P', 'q', 'Q', 'w', 'W', 's', 'S', 'x', 'X',
  // Modifier-friendly: still allow shift (used for shift+F dev shortcut).
  'Shift', 'Control', 'Meta', 'Alt',
  // Reasonable navigation that we don't want blocked.
  ' ', 'Enter', 'Escape', 'Backspace',
]);
window.addEventListener(
  'keydown',
  (e) => {
    // Don't interfere with text input — the score form needs all keys.
    const t = e.target as HTMLElement | null;
    const tag = t?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || t?.isContentEditable) return;
    if (!GAME_KEY_ALLOWLIST.has(e.key)) {
      e.preventDefault();
    }
  },
  // Capture phase so Phaser's listeners and form listeners both still see
  // the events on allowed keys; we just block the browser default.
  { capture: true },
);

export default game;
